/**
 * Remembers the last path within each top-level section (operator / console)
 * so the sidebar's cross-section link can return the user to where they left
 * off rather than the section root.
 */

const STORAGE_PREFIX = 'lastPath:';

/**
 * Section keys are either a top-level surface (`'operator'`, `'console'`) or a
 * per-space console key (`'console:spc-abc'`) so each space remembers its own
 * last visited path.
 */
export type Section = 'operator' | 'console' | `console:${string}`;

/** Decide whether a stored path still belongs to the named section. Entries
 *  that don't are dropped on read — this protects against URL restructures
 *  (e.g. `/console/...` → `/c/...`, or `/operator/...` → `/...`) leaving
 *  behind stale entries in `localStorage` that would navigate to dead routes. */
function isStoredPathValid(section: Section, stored: string): boolean {
  if (section.startsWith('console')) {
    // Match at a path boundary — `/c` must not match `/console/...`.
    return stored === '/c' || stored.startsWith('/c/');
  }
  // Operator lives at the root. Anything not owned by another section counts —
  // and drop legacy `/operator/...` entries from before the URL flattening.
  return !stored.startsWith('/c') && !stored.startsWith('/operator');
}

export function rememberPath(section: Section, path: string): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + section, path);
  } catch {
    // localStorage unavailable (private mode, quota); silently ignore.
  }
}

export function recallPath(section: Section, fallback: string): string {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + section);
    if (!stored) return fallback;
    if (!isStoredPathValid(section, stored)) {
      localStorage.removeItem(STORAGE_PREFIX + section);
      return fallback;
    }
    return stored;
  } catch {
    return fallback;
  }
}
